/**
 * Document Review Queue page — Vision pass admin view.
 *
 * Shows uploads that fell below the tenant's confidence threshold or
 * landed in an unexpected category. Owners can:
 *   - Resolve (the doc is correctly filed where it is)
 *   - Dismiss (low-confidence is acceptable here)
 *   - Open the source doc to re-categorise
 *
 * Route: /settings/document-review-queue
 */
import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, AlertTriangle, CheckCircle2, X, ExternalLink, Eye } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useBranding } from "../context/TenantBrandingContext";

const SURFACE_LABELS = {
  sales_doc: "Sales document",
  expense: "Expense receipt",
  invoice: "Supplier invoice",
  cv: "Candidate CV",
  datasheet: "Datasheet",
};

const SURFACE_LINKS = {
  sales_doc: (id) => `/sales-documents/${id}/edit`,
  expense: (id) => `/expenses/${id}`,
  invoice: (id) => `/sales-documents/${id}/edit`,
  cv: (id) => `/recruitment/candidates/${id}`,
  datasheet: (id) => `/datasheets/${id}`,
};

const REASON_BADGE = {
  low_confidence: { label: "Low confidence", color: "amber" },
  mismatch: { label: "Type mismatch", color: "rose" },
  low_confidence_mismatch: { label: "Low conf + mismatch", color: "rose" },
};

function ReasonBadge({ reason }) {
  const cfg = REASON_BADGE[reason] || { label: reason, color: "slate" };
  const colorMap = {
    amber: "bg-amber-500/15 border-amber-500/40 text-amber-200",
    rose: "bg-rose-500/15 border-rose-500/40 text-rose-200",
    slate: "bg-slate-500/15 border-slate-500/40 text-slate-200",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] border px-1.5 py-0.5 rounded ${colorMap[cfg.color]}`}
    >
      <AlertTriangle className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

export default function DocumentReviewQueue() {
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("open");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/document-review-queue?status=${statusFilter}`);
      setItems(r.data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const act = useCallback(async (id, action) => {
    try {
      await api.post(`/document-review-queue/${id}/${action}`);
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch (e) {
      console.warn(`queue ${action} failed`, e);
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#0d101a] text-[#dde3ec] pb-24">
      <div className="max-w-4xl mx-auto px-4 pt-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-xs text-[#647087] hover:text-[#1fb6c1] inline-flex items-center gap-1 mb-4"
          data-testid="queue-back-button"
        >
          <ArrowLeft className="w-3 h-3" />
          Back
        </button>

        <div className="flex items-start gap-3 mb-2">
          <div className="w-10 h-10 rounded-md bg-[#1fb6c1]/15 border border-[#1fb6c1]/40 flex items-center justify-center">
            <Eye className="w-5 h-5 text-[#1fb6c1]" />
          </div>
          <div>
            <h1 className="font-display text-3xl font-bold">Document review queue</h1>
            <p className="text-sm text-[#a0aabf] mt-1 max-w-prose">
              Uploads {aiName} Vision wasn&apos;t sure about. Open each one, confirm
              it&apos;s in the right place — or move it. Resolved items disappear from this list.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-4 mb-6">
          {["open", "resolved", "dismissed", "all"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              data-testid={`queue-filter-${s}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                statusFilter === s
                  ? "bg-[#1fb6c1]/15 border-[#1fb6c1]/40 text-[#1fb6c1]"
                  : "bg-transparent border-[#2a3142] text-[#a0aabf] hover:border-[#1fb6c1]/40"
              }`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="hv-card p-8 text-center text-sm text-[#647087]">Loading queue…</div>
        ) : items.length === 0 ? (
          <div className="hv-card p-8 text-center" data-testid="queue-empty">
            <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
            <div className="font-display text-lg font-semibold">All clear</div>
            <div className="text-xs text-[#647087] mt-1">
              No documents currently in the review queue.
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="queue-list">
            {items.map((it) => {
              const cls = it.classification || {};
              const surface = it.surface || "unknown";
              const link = SURFACE_LINKS[surface]?.(it.doc_id);
              return (
                <div
                  key={it.id}
                  className="hv-card p-4"
                  data-testid={`queue-item-${it.id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <ReasonBadge reason={it.reason} />
                        <span className="text-[10px] uppercase tracking-[0.18em] text-[#647087]">
                          {SURFACE_LABELS[surface] || surface}
                        </span>
                      </div>
                      <div className="font-display text-base font-semibold truncate">
                        {it.label || it.doc_id}
                      </div>
                      <div className="mt-2 text-xs text-[#a0aabf] space-y-1">
                        <div>
                          Hidro says:{" "}
                          <span className="font-mono text-[#dde3ec]">
                            {cls.doc_type || "unknown"}
                          </span>{" "}
                          ·{" "}
                          <span className="font-mono">
                            {Math.round((cls.confidence || 0) * 100)}%
                          </span>
                          {it.expected_type && (
                            <>
                              {" "}· expected{" "}
                              <span className="font-mono text-[#dde3ec]">{it.expected_type}</span>
                            </>
                          )}
                        </div>
                        {cls.reasoning && (
                          <div className="italic text-[#647087]">&ldquo;{cls.reasoning}&rdquo;</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {it.status === "open" && (
                    <div className="flex items-center gap-2 mt-4 pt-3 border-t border-[#2a3142]">
                      {link && (
                        <Link
                          to={link}
                          className="hv-btn hv-btn-ghost text-xs inline-flex items-center gap-1"
                          data-testid={`queue-open-${it.id}`}
                        >
                          <ExternalLink className="w-3 h-3" />
                          Open
                        </Link>
                      )}
                      <button
                        type="button"
                        onClick={() => act(it.id, "resolve")}
                        className="hv-btn hv-btn-primary text-xs inline-flex items-center gap-1"
                        data-testid={`queue-resolve-${it.id}`}
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        Resolve
                      </button>
                      <button
                        type="button"
                        onClick={() => act(it.id, "dismiss")}
                        className="hv-btn hv-btn-ghost text-xs inline-flex items-center gap-1 text-[#647087]"
                        data-testid={`queue-dismiss-${it.id}`}
                      >
                        <X className="w-3 h-3" />
                        Dismiss
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
